import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {alpha, useTheme} from '@mui/material/styles';
import {LoadingButton} from '@mui/lab';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import PauseIcon from '@mui/icons-material/Pause';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import {useTranslation} from 'react-i18next';
import {AutomationFlow, AutomationStatus} from 'crqlar-react-common/dist/models/automation/automation';

import {AutomationStatusElement} from '../../../common/uiElements/AutomationStatusElement';

interface AutomationEditorHeaderProps {
    flow?: AutomationFlow;
    readOnly: boolean;
    isDirty: boolean;
    onBack: () => void;
    /** The Preview button shows only while the canvas has something to preview. */
    previewable: boolean;
    previewOn: boolean;
    onTogglePreview: () => void;
    testRunBusy: boolean;
    onTestRun: () => void;
    onGoLive: () => void;
    saving: boolean;
    onSave: () => void;
    /** Held by the editor: an open menu pauses the canvas shortcuts. */
    menuAnchor: HTMLElement | null;
    onMenuAnchorChange: (anchor: HTMLElement | null) => void;
    renameDisabled: boolean;
    onRename: () => void;
    onPause: () => void;
    onDelete: () => void;
}

const AutomationEditorHeader: React.FC<AutomationEditorHeaderProps> = ({
    flow,
    readOnly,
    isDirty,
    onBack,
    previewable,
    previewOn,
    onTogglePreview,
    testRunBusy,
    onTestRun,
    onGoLive,
    saving,
    onSave,
    menuAnchor,
    onMenuAnchorChange,
    renameDisabled,
    onRename,
    onPause,
    onDelete
}) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const isActive = flow?.status === AutomationStatus.ACTIVE;

    return (
        <Stack
            direction={'row'}
            alignItems={'center'}
            gap={1}
            sx={{
                px: 2,
                minHeight: 56,
                py: {xs: 1, md: 0},
                flexShrink: 0,
                flexWrap: {xs: 'wrap', md: 'nowrap'},
                position: 'relative',
                borderBottom: `1px solid ${theme.palette.divider}`,
                // Live accent drawn with ::after, not a border, so going live shifts nothing.
                ...(isActive && {
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        height: '2px',
                        backgroundColor: alpha(theme.palette.success.main, 0.65),
                        pointerEvents: 'none'
                    }
                })
            }}
        >
            <IconButton aria-label={t('Back')} onClick={onBack}>
                <ArrowBackIcon/>
            </IconButton>

            <Typography variant={'h6'} noWrap sx={{minWidth: 0}}>
                {flow?.name ?? ''}
            </Typography>

            <Box sx={{flex: 1}}/>

            <AutomationStatusElement status={flow?.status} live/>

            {/* A button, not a switch: a switch beside the status chip reads as "go live". */}
            {previewable && (
                <Tooltip title={previewOn ? '' : t('Preview tooltip')}>
                    <Button
                        variant={'text'}
                        color={previewOn ? 'info' : 'primary'}
                        startIcon={<VisibilityOutlinedIcon/>}
                        onClick={onTogglePreview}
                        aria-pressed={previewOn}
                        sx={{
                            flexShrink: 0,
                            whiteSpace: 'nowrap',
                            ...(previewOn && {bgcolor: alpha(theme.palette.info.main, 0.12)}),
                        }}
                    >
                        {t('Preview')}
                    </Button>
                </Tooltip>
            )}

            {/* Tooltip only while disabled, to say why. */}
            <Tooltip title={isDirty
                ? t('Save your changes first')
                : testRunBusy ? t('A test run is in progress') : ''}>
                <span>
                    <Button
                        variant={'text'}
                        startIcon={<PlayArrowRoundedIcon/>}
                        onClick={onTestRun}
                        disabled={!flow || readOnly || isDirty || testRunBusy}
                    >
                        {t('Test run')}
                    </Button>
                </span>
            </Tooltip>

            {/* No Runs button: the canvas's Runs panel links to them ("View all"). */}
            <LoadingButton
                variant={'contained'}
                loading={saving}
                disabled={!isDirty || readOnly}
                onClick={onSave}
            >
                {t('Save')}
            </LoadingButton>

            <IconButton
                aria-label={t('More')}
                onClick={event => onMenuAnchorChange(event.currentTarget)}
            >
                <MoreVertIcon/>
            </IconButton>

            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => onMenuAnchorChange(null)}
            >
                <MenuItem
                    disabled={renameDisabled}
                    onClick={() => {
                        onMenuAnchorChange(null);
                        onRename();
                    }}
                >
                    <ListItemIcon><DriveFileRenameOutlineIcon fontSize="small"/></ListItemIcon>
                    <ListItemText>{t('Rename')}</ListItemText>
                </MenuItem>

                {/* Go live and Pause share the menu slot: one or the other, by status. */}
                {!isActive && (
                    <MenuItem
                        disabled={!flow || readOnly}
                        onClick={() => {
                            onMenuAnchorChange(null);
                            onGoLive();
                        }}
                    >
                        <ListItemIcon><PlayCircleOutlineIcon fontSize={'small'} color={'success'}/></ListItemIcon>
                        <ListItemText>{t('Go live')}</ListItemText>
                    </MenuItem>
                )}

                {isActive && (
                    <MenuItem
                        disabled={!flow || readOnly}
                        onClick={() => {
                            onMenuAnchorChange(null);
                            onPause();
                        }}
                    >
                        <ListItemIcon><PauseIcon fontSize={'small'}/></ListItemIcon>
                        <ListItemText>{t('Pause')}</ListItemText>
                    </MenuItem>
                )}

                <Divider/>

                <MenuItem
                    disabled={!flow || readOnly}
                    onClick={() => {
                        onMenuAnchorChange(null);
                        onDelete();
                    }}
                >
                    <ListItemIcon>
                        <DeleteIcon fontSize={'small'} color={'error'}/>
                    </ListItemIcon>
                    <ListItemText>{t('Delete')}</ListItemText>
                </MenuItem>
            </Menu>
        </Stack>
    );
};

export default AutomationEditorHeader;
